import React, { FC, ReactNode, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { useReactForm, ValuationHooks, VestingTaskHooks } from '../../../../hooks/index';
import { SharePlanType, sharePlanTypeTitle } from '../../../../types/pool-plans.types';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { AppFormattedMessage } from '../../../app-formatted-message/index';
import { CloseModalButton } from '../../../close-modal-button/index';
import { Sheet, SheetContent } from '../../../sheet/index';
import { ChildrenFormProps } from './exercise-form.types';
import { FormSchema, formSchema } from './exercise-form.validation';
import { StepOne } from './step-one';
import { StepTwo } from './step-two';

export type ExerciseFormProps = {
  companyId: string;
  handleClose: () => void;
  id: string;
  invalidateQuery: () => void;
  isOpen: boolean;
  type: SharePlanType;
};

const formSteps: ((props: ChildrenFormProps) => ReactNode)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
];
export const ExerciseForm: FC<ExerciseFormProps> = ({
  isOpen,
  companyId,
  handleClose,
  invalidateQuery,
  type,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    resetField,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
  });

  const { valuation } = ValuationHooks.useCurrent();

  const { create: exercise, isPending } = VestingTaskHooks.useExercise();

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
    setCurrentStep(1);
  }, [reset, handleClose]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepTwo } = data;

      const hurdle =
        type === SharePlanType.GROWTH_HURDLE &&
        stepOne.hurdle?.hurdleItems &&
        stepOne.hurdle?.hurdleItems.length > 0 &&
        stepOne.hurdle?.hurdleItems.map(({ issuedShares, grant, date }) => ({
          vestingTaskId: grant.id,
          value: issuedShares,
          date: toBackendDateFormat(date),
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
        }));

      // const rsa =
      //   type === SharePlanType.RSA &&
      //   stepOne.rsa?.rsaItems &&
      //   stepOne.rsa?.rsaItems.length > 0 &&
      //   stepOne.rsa?.rsaItems.map(({ exercised, grant }) => ({
      //     vestingTaskId: grant.id,
      //     value: exercised,
      //     filesLinks:
      //       stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
      //   }));

      const stock =
        type === SharePlanType.STOCK_OPTIONS &&
        stepOne.commonStock?.stockItems &&
        stepOne.commonStock.stockItems.length > 0 &&
        stepOne.commonStock?.stockItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
        }));

      const warrantExercise =
        type === SharePlanType.WARRANTS &&
        stepOne.warrantExercise?.warrantExerciseItems &&
        stepOne.warrantExercise.warrantExerciseItems.length > 0 &&
        stepOne.warrantExercise?.warrantExerciseItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
        }));

      const bspce =
        type === SharePlanType.BSPCE &&
        stepOne.bspce?.bspceItems &&
        stepOne.bspce?.bspceItems.length > 0 &&
        stepOne.bspce?.bspceItems.map(({ exercised, grant, date }) => ({
          vestingTaskId: grant.id,
          value: exercised,
          date: toBackendDateFormat(date),
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
        }));

      const sar =
        type === SharePlanType.SAR &&
        stepOne.sar?.sarItems &&
        stepOne.sar?.sarItems.length > 0 &&
        stepOne.sar?.sarItems.map(({ sarCount, grant, sarPayout, sharePrice, date }) => ({
          vestingTaskId: grant.id,
          value: sarCount,
          payout: sarPayout,
          sarSharePrice: sharePrice,
          date: toBackendDateFormat(date),
          filesLinks: [],
        }));

      const exerciseData = hurdle || stock || warrantExercise || sar || bspce;

      if (exerciseData) {
        exercise(
          {
            companyId,
            data: exerciseData,
          },
          {
            onSuccess: () => {
              toast.success(<AppFormattedMessage id="EXERCISED_SUCCESSFULLY" />);
              invalidateQuery();
              handleCloseModal();
            },
          },
        );
      }
    },
    [companyId, exercise, handleCloseModal, invalidateQuery, type],
  );

  const handleNextStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep === Object.keys(formSteps).length + 1) {
      handleSubmit(submitHandler, handleFormInvalid)();
      return;
    }
    if (nextStep > Object.keys(formSteps).length) return;

    setCurrentStep(nextStep);
  }, [currentStep, handleSubmit, submitHandler]);

  const handlePrevStep = useCallback(() => {
    const prevStep = currentStep - 1;
    if (prevStep < 1) return;
    setCurrentStep(prevStep);
  }, [currentStep]);

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpen}>
      <SheetContent
        className="h-fit max-h-[800px] w-[90%] max-w-[1422px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="CENTER"
      >
        <div className="flex h-fit w-full flex-col gap-8 rounded-2xl bg-white p-8">
          <div className="flex h-fit w-full items-center justify-between">
            <span className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="EXERCISE" />{' '}
              {type && <AppFormattedMessage id={sharePlanTypeTitle[type]} />}
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex h-full max-h-[520px] flex-col overflow-y-auto">
            {formSteps[currentStep - 1]({
              companyId,
              formData: getValues,
              nextFormStep: handleNextStep,
              prevFormStep: handlePrevStep,
              setFormData: setValue,
              control,
              clearErrors,
              handleCloseModal,
              setError,
              filedState: getFieldState,
              errors,
              type,
              resetField,
              reset,
              valuation,
              isLoading: isPending,
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
